import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import TabMenu from '../../navigation/TabMenu'
import SettingTabsInfo from '../../../constants/SettingTabsInfo'
import SearchBar from '../../overview/SearchBar'
import ReloadButton from '../../overview/ReloadButton'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { showModal } from '../../../modules/UI'
import { BASE_MODAL } from '../../common/RootModal'
import { getGroupQrTags } from '../../../modules/QrTag'
import { getErrorString } from '../../../utils/errorUtils'
import { pickQrTagEnabledGroups, teamHasQrTags } from '../../../helpers/qrTagFeature'
import QrTagSettingList from './QrTagSettingList'
import EditQrTagModal from './EditQrTagModal'
import QrTagEventTypesEditor from './QrTagEventTypesEditor'
import './SettingsQrTags.css'

type TagWithGroup = IQrTag & { group?: IGroup }

const SettingsQrTags = () => {
  const dispatch = useAppDispatch()
  const team = useAppSelector((state) => state.team)

  const [keyword, setKeyword] = useState('')
  const [qrTags, setQrTags] = useState<TagWithGroup[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasQrTagFeature = teamHasQrTags(team)
  const qrTagGroups = useMemo(() => pickQrTagEnabledGroups(team.groups), [team.groups])
  const hasSelectableGroup = qrTagGroups.length > 0
  const showEventTypeSettings = true

  const fetchQrTags = useCallback(async (groups: IGroup[], searchKeyword: string) => {
    if (groups.length === 0) {
      setQrTags([])
      return
    }

    setLoading(true)
    try {
      const results = await Promise.all(groups.map(async (group) => {
        const tags = await getGroupQrTags(group.id, searchKeyword)
        return tags.map((tag) => ({ ...tag, group }))
      }))
      const flattened = results.flat()
      setQrTags(flattened)
      setError(null)
    } catch (err) {
      setError(getErrorString(err))
      setQrTags([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!hasQrTagFeature) return

    fetchQrTags(qrTagGroups, keyword)
  }, [fetchQrTags, hasQrTagFeature, keyword, qrTagGroups])

  const handleReload = useCallback(async () => {
    await fetchQrTags(qrTagGroups, keyword)
  }, [fetchQrTags, keyword, qrTagGroups])

  const tabsInfo = useMemo(
    () => SettingTabsInfo({ teamId: team.id ?? null, groups: team.groups ?? null }),
    [team.groups, team.id],
  )

  const stableLoadList = useCallback((useDeviceApi: boolean) => {
    // SearchBarのシグネチャ合わせ。QRタグではdevice切替は使わない。
    if (useDeviceApi !== undefined) {
      // noop
    }
    handleReload()
  }, [handleReload])

  const handleEdit = useCallback((qrTag: TagWithGroup) => {
    if (!qrTag.group) return

    dispatch(showModal({
      modalType: BASE_MODAL,
      modalProps: {
        title: 'QRタグを編集',
        qrTag,
        group: qrTag.group,
        onUpdated: (updatedTag: IQrTag) => {
          setQrTags((prev) => prev.map(
            (tag) => (tag.id === updatedTag.id ? { ...updatedTag, group: tag.group } : tag),
          ))
        },
        children: (
          <EditQrTagModal />
        ),
      },
    }))
  }, [dispatch])

  if (!hasQrTagFeature) {
    return (
      <TabMenu tabsInfo={tabsInfo}>
        <div className="qr-tag-settings-page">
          <h2 className="qr-tag-settings__title">QRタグの設定</h2>
          <p className="qr-tag-settings__description">
            QRタグ機能が有効なチームのみ利用できます。権限については管理者にお問い合わせください。
          </p>
        </div>
      </TabMenu>
    )
  }

  return (
    <TabMenu tabsInfo={tabsInfo}>
      <div className="qr-tag-settings-page">
        <div className="qr-tag-settings__header">
          <div>
            <h2 className="qr-tag-settings__title">QRタグの設定</h2>
            <p className="qr-tag-settings__description">
              QRタグの名前や説明を編集できます。チーム内のQRタグを横断して検索できます。
            </p>
          </div>
          <div className="qr-tag-settings__controls">
            <ReloadButton onClick={handleReload} />
          </div>
        </div>
        {hasSelectableGroup ? (
          <>
            {showEventTypeSettings && (
              <QrTagEventTypesEditor groups={qrTagGroups} />
            )}
            <div className="qr-tag-settings__search">
              <SearchBar
                rentalMode={false}
                loadList={stableLoadList}
                updateGroupFilter={() => {}}
                showDeviceSwitch={false}
                showSelector={false}
                currentListType="qr_tag"
                memoEnabled={false}
                listTypes={['qr_tag']}
                qrTagKeyword={keyword}
                onQrTagSearch={setKeyword}
              />
            </div>
            <QrTagSettingList
              qrTags={qrTags}
              loading={loading}
              error={error}
              onEdit={handleEdit}
            />
          </>
        ) : (
          <p className="qr-tag-settings__description">
            QRタグが有効なグループがありません。管理者がQRタグを割り当てると一覧が表示されます。
          </p>
        )}
      </div>
    </TabMenu>
  )
}

export default SettingsQrTags
